import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// ─── Right Panel (setter and value are separate contexts) ───
const RightPanelValueContext = createContext<ReactNode | null>(null);
const RightPanelSetterContext = createContext<(content: ReactNode | null) => void>(() => {});

// ─── Compact Header (setter and value are separate contexts) ───
const CompactHeaderValueContext = createContext<ReactNode | null>(null);
const CompactHeaderSetterContext = createContext<(content: ReactNode | null) => void>(() => {});

// ─── Force Compact Header (always show compact content, hide logo) ───
const ForceCompactValueContext = createContext<boolean>(false);
const ForceCompactSetterContext = createContext<(force: boolean) => void>(() => {});

// ─── Header Hidden (for pages with their own header, e.g. NoteEditor) ───
const HeaderHiddenValueContext = createContext<boolean>(false);
const HeaderHiddenSetterContext = createContext<(hidden: boolean) => void>(() => {});

// ─── Provider ───
export function MobilePanelProvider({ children }: { children: ReactNode }) {
  const [rightPanelContent, setRightPanelContentState] = useState<ReactNode | null>(null);
  const [compactHeaderContent, setCompactHeaderContentState] = useState<ReactNode | null>(null);
  const [forceCompact, setForceCompactState] = useState(false);
  const [headerHidden, setHeaderHiddenState] = useState(false);

  const setRightPanelContent = useCallback((content: ReactNode | null) => {
    setRightPanelContentState(content);
  }, []);

  const setCompactHeaderContent = useCallback((content: ReactNode | null) => {
    setCompactHeaderContentState(content);
  }, []);

  const setForceCompact = useCallback((force: boolean) => {
    setForceCompactState(force);
  }, []);

  const setHeaderHidden = useCallback((hidden: boolean) => {
    setHeaderHiddenState(hidden);
  }, []);

  return (
    <RightPanelSetterContext.Provider value={setRightPanelContent}>
      <RightPanelValueContext.Provider value={rightPanelContent}>
        <CompactHeaderSetterContext.Provider value={setCompactHeaderContent}>
          <CompactHeaderValueContext.Provider value={compactHeaderContent}>
            <ForceCompactSetterContext.Provider value={setForceCompact}>
              <ForceCompactValueContext.Provider value={forceCompact}>
                <HeaderHiddenSetterContext.Provider value={setHeaderHidden}>
                  <HeaderHiddenValueContext.Provider value={headerHidden}>
                    {children}
                  </HeaderHiddenValueContext.Provider>
                </HeaderHiddenSetterContext.Provider>
              </ForceCompactValueContext.Provider>
            </ForceCompactSetterContext.Provider>
          </CompactHeaderValueContext.Provider>
        </CompactHeaderSetterContext.Provider>
      </RightPanelValueContext.Provider>
    </RightPanelSetterContext.Provider>
  );
}

// ─── Hooks ───

/** Use in components that only WRITE right panel content (e.g. PageLayout) */
export function useSetRightPanel() {
  return useContext(RightPanelSetterContext);
}

/** Use in components that only READ right panel content (e.g. MainLayout drawer) */
export function useRightPanelContent() {
  return useContext(RightPanelValueContext);
}

/** Use in components that only WRITE compact header content (e.g. pages) */
export function useSetCompactHeader() {
  return useContext(CompactHeaderSetterContext);
}

/** Use in components that only READ compact header content (e.g. Header) */
export function useCompactHeaderContent() {
  return useContext(CompactHeaderValueContext);
}

/** Use in pages that want compact header always visible (e.g. track detail) */
export function useSetForceCompact() {
  return useContext(ForceCompactSetterContext);
}

/** Use in Header to check if compact content should always show */
export function useForceCompact() {
  return useContext(ForceCompactValueContext);
}

/** Use in pages that have their own header and want to hide the main Header (e.g. NoteEditor) */
export function useSetHeaderHidden() {
  return useContext(HeaderHiddenSetterContext);
}

/** Use in Header to check if it should be hidden */
export function useHeaderHidden() {
  return useContext(HeaderHiddenValueContext);
}
